import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// Initialize Firebase Functions for Twilio operations
const sendSMSFunction = httpsCallable(functions, 'sendSMS');
const sendWhatsAppFunction = httpsCallable(functions, 'sendWhatsApp');
const makeVoiceCallFunction = httpsCallable(functions, 'makeVoiceCall');

// Rate limiting configuration
const RATE_LIMIT = {
  sms: { maxPerMinute: 60, maxPerHour: 1000 },
  whatsapp: { maxPerMinute: 20, maxPerHour: 500 },
  voice: { maxPerMinute: 10, maxPerHour: 200 }
};

// Retry configuration
const RETRY_CONFIG = {
  maxAttempts: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffFactor: 2
};

// Message queue for rate limiting
const messageQueue = {
  sms: [],
  whatsapp: [],
  voice: []
};

// Rate limiting implementation
const rateLimiter = {
  counters: {
    sms: { minute: 0, hour: 0 },
    whatsapp: { minute: 0, hour: 0 },
    voice: { minute: 0, hour: 0 }
  },
  timers: {
    minute: null,
    hour: null
  },

  init() {
    // Reset minute counter every minute
    this.timers.minute = setInterval(() => {
      Object.keys(this.counters).forEach(channel => {
        this.counters[channel].minute = 0;
      });
    }, 60000);

    // Reset hour counter every hour
    this.timers.hour = setInterval(() => {
      Object.keys(this.counters).forEach(channel => {
        this.counters[channel].hour = 0;
      });
    }, 3600000);
  },

  canSend(channel) {
    const limits = RATE_LIMIT[channel];
    return (
      this.counters[channel].minute < limits.maxPerMinute &&
      this.counters[channel].hour < limits.maxPerHour
    );
  },

  increment(channel) {
    this.counters[channel].minute++;
    this.counters[channel].hour++;
  }
};

// Initialize rate limiter
rateLimiter.init();

// Retry mechanism
const retry = async (operation, channel) => {
  let attempt = 0;
  let delay = RETRY_CONFIG.initialDelay;

  while (attempt < RETRY_CONFIG.maxAttempts) {
    try {
      if (!rateLimiter.canSend(channel)) {
        await new Promise(resolve => setTimeout(resolve, 60000)); // Wait a minute
        continue;
      }

      const result = await operation();
      rateLimiter.increment(channel);
      return result;
    } catch (error) {
      attempt++;
      if (attempt === RETRY_CONFIG.maxAttempts) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * RETRY_CONFIG.backoffFactor, RETRY_CONFIG.maxDelay);
    }
  }
};

export const twilioService = {
  // Send SMS message with retry and rate limiting
  async sendSMS(to, body, campaignId, recipientId) {
    try {
      const result = await retry(
        () => sendSMSFunction({
          to,
          body,
          campaignId,
          recipientId
        }),
        'sms'
      );

      const message = result.data;

      await this.logMessageAttempt({
        campaignId,
        recipientId,
        channel: 'sms',
        status: message.status,
        sid: message.sid,
        to,
        body,
        attempt: 1
      });

      return message;
    } catch (error) {
      console.error('Error sending SMS:', error);
      
      await this.logMessageAttempt({
        campaignId,
        recipientId,
        channel: 'sms',
        status: 'failed',
        error: error.message,
        to,
        body,
        attempt: RETRY_CONFIG.maxAttempts
      });

      throw new Error(`SMS delivery failed after ${RETRY_CONFIG.maxAttempts} attempts: ${error.message}`);
    }
  },

  // Send WhatsApp message with retry and rate limiting
  async sendWhatsApp(to, body, campaignId, recipientId) {
    try {
      const result = await retry(
        () => sendWhatsAppFunction({
          to,
          body,
          campaignId,
          recipientId
        }),
        'whatsapp'
      );

      const message = result.data;

      await this.logMessageAttempt({
        campaignId,
        recipientId,
        channel: 'whatsapp',
        status: message.status,
        sid: message.sid,
        to,
        body,
        attempt: 1
      });

      return message;
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      
      await this.logMessageAttempt({
        campaignId,
        recipientId,
        channel: 'whatsapp',
        status: 'failed',
        error: error.message,
        to,
        body,
        attempt: RETRY_CONFIG.maxAttempts
      });

      throw new Error(`WhatsApp delivery failed after ${RETRY_CONFIG.maxAttempts} attempts: ${error.message}`);
    }
  },

  // Make voice call with retry and rate limiting
  async makeVoiceCall(to, twiml, campaignId, recipientId) {
    try {
      const result = await retry(
        () => makeVoiceCallFunction({
          to,
          twiml,
          campaignId,
          recipientId
        }),
        'voice'
      );

      const call = result.data;

      await this.logMessageAttempt({
        campaignId,
        recipientId,
        channel: 'voice',
        status: call.status,
        sid: call.sid,
        to,
        body: twiml,
        attempt: 1
      });

      return call;
    } catch (error) {
      console.error('Error making voice call:', error);
      
      await this.logMessageAttempt({
        campaignId,
        recipientId,
        channel: 'voice',
        status: 'failed',
        error: error.message,
        to,
        body: twiml,
        attempt: RETRY_CONFIG.maxAttempts
      });

      throw new Error(`Voice call failed after ${RETRY_CONFIG.maxAttempts} attempts: ${error.message}`);
    }
  },

  // Email functionality removed - emails handled by Firebase Auth
  // Use Firebase Auth sendPasswordResetEmail for user invitations

  // Enhanced message logging with more details
  async logMessageAttempt(data) {
    try {
      const { campaignId, recipientId, ...messageData } = data;
      
      await addDoc(
        collection(db, `organizations/${campaignId}/recipients/${recipientId}/messages`),
        {
          ...messageData,
          timestamp: serverTimestamp(),
          environment: process.env.NODE_ENV,
          version: process.env.REACT_APP_VERSION
        }
      );
    } catch (error) {
      console.error('Error logging message attempt:', error);
      // Don't throw here to prevent cascading failures
    }
  },

  // Enhanced message status update with more details
  async updateMessageStatus(campaignId, recipientId, messageId, status, response = null) {
    try {
      const messageRef = doc(
        db, 
        `organizations/${campaignId}/recipients/${recipientId}/messages`,
        messageId
      );

      await updateDoc(messageRef, {
        status,
        response,
        updatedAt: serverTimestamp(),
        lastUpdateAttempt: serverTimestamp(),
        environment: process.env.NODE_ENV
      });
    } catch (error) {
      console.error('Error updating message status:', error);
      // Don't throw here to prevent cascading failures
    }
  },

  // Cleanup function for rate limiter
  cleanup() {
    if (rateLimiter.timers.minute) {
      clearInterval(rateLimiter.timers.minute);
    }
    if (rateLimiter.timers.hour) {
      clearInterval(rateLimiter.timers.hour);
    }
  }
}; 